<template>
  <div class="quickreply-component" v-click-away="setUnclickedState" @click="setClickedState">
    <IslandBadge type="quickreply" :draggable="false"></IslandBadge>
    <div class="component-contents">
      <div class="message-part">
        <TextEditor
          ref="textEditor"
          :value="state.text"
          :placeholder="'Enter a message...'"
          @text-updated="textUpdated"
        />

        <p v-if="isTextRequiredInvalid" class="help is-danger">Message text must not be empty</p>
      </div>

      <Draggable v-model="state.options" class="reply-options-list" item-key="id">
        <template #item="{ element: option, index }">
          <QuickReplyOption
            :key="option.id"
            :option="option"
            :index="index"
            :all-options="state.options"
            :pattern="state.pattern"
            :data-type-error-message="getDataTypeErrorMessage()"
            :has-data-type="hasDataType()"
            :is-valid-transition="isValidTransition"
            @text-updated="optionUpdated"
            @transition-selected="updateOptionTransition"
            @delete-option="deleteOption"
          />
        </template>
      </Draggable>

      <p class="help is-danger" v-if="isOptionsRequiredInvalid">Must have at least one option</p>

      <div class="reply-option-add">
        <n-tooltip :text="getAddTooltipText()">
          <a @click="addOption" class="add-button" :class="{ disabled: !canAddMoreOptions() }">
            <AddIcon class="icon-1-3x"></AddIcon>
          </a>
        </n-tooltip>
      </div>

      <AdditionalInformationWrapper v-show="showConfig" color="#FF6666" text-color="#FFFFFF">
        <template #content>
          <InputValidationConfig
            :value="state"
            :fields="['errorMessage', 'allowedAttempts', 'noMatch']"
            @updated="additionalConfigChanged"
          />

          <fieldset>
            <div class="field is-horizontal">
              <div class="field-label">
                <label class="label">Store result</label>
              </div>
              <div class="field-body">
                <div class="control">
                  <VariablePicker :value="state.storedVariable" @variable-selected="variableSelected"></VariablePicker>

                  <p v-if="isAttributeMissingInvalid" class="help is-danger">Attribute does not exist</p>
                  <p v-if="isAttributeArchivedInvalid" class="help is-danger">Attribute has been archived</p>
                </div>
              </div>
            </div>
          </fieldset>
        </template>
      </AdditionalInformationWrapper>

      <transition :name="transition">
        <div v-if="hasClicked && !showConfig" class="expander" @click="showExpandedConfig">
          <ChevronRight class="chevron"> </ChevronRight>
        </div>
      </transition>

      <div v-if="hasClicked && showConfig" class="expander" @click="hideExpandedConfig">
        <ChevronLeft class="chevron"> </ChevronLeft>
      </div>

      <div class="invalid-warning" :class="{ 'shift-for-expander': hasClicked }" v-if="state.invalid && !showConfig">
        <ErrorIcon class="icon-1-3x"></ErrorIcon>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Draggable from 'vuedraggable';
import { nanoid } from 'nanoid';
import { FlowViewModel, InputVariableViewModel, StateViewModel } from '@/features/bots/models/designer';
import QuickReplyOption from './QuickReplyOption.vue';
import IslandBadge from './IslandBadge.vue';
import AdditionalInformationWrapper from './AdditionalInformationWrapper.vue';
import InputValidationConfig from './InputValidationConfig.vue';
import VariablePicker from './VariablePicker/VariablePicker.vue';
import ChevronRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import AddIcon from 'vue-material-design-icons/PlusCircle.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import TextEditor from '@/features/bots/components/designer/TextEditor/TextEditor.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { RegexType } from '../../models/designer/regexType';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'state-updated', value: StateViewModel): void;
}>();

defineExpose({
  focusOnInput,
});

const store = useStore();
const doesAttributeExist = (attributeId: string) =>
  computed<boolean>(() => store.getters.doesAttributeExist(attributeId));
const isAttributeArchived = (attributeId: string) =>
  computed<boolean>(() => store.getters.isAttributeArchived(attributeId));
const currentFlow = computed<FlowViewModel>(() => store.getters.currentFlow);
const loadFlows = (doLoad = false): Promise<FlowListItemViewModel[]> => store.dispatch(LOAD_FLOWS, doLoad);

const showConfig = ref(false);
const hasClicked = ref(false);
const transition = ref('display');
const flows = ref<FlowListItemViewModel[]>([]);
const textEditor = ref<typeof TextEditor | null>(null);
const state = ref<StateViewModel>({
  id: '',
  component: 'InputString',
  allowedAttempts: 1,
  options: [],
});
const additionalConfigInvalid = ref(false);

const validations = computed(() => ({
  state: {
    text: {
      required,
    },
    options: {
      required,
    },
    storedVariable: {
      attributeMissing(value: InputVariableViewModel) {
        const storedVar: InputVariableViewModel = state.value.storedVariable;
        return !storedVar || storedVar.scope !== 'appUser.customAttributes' || doesAttributeExist(storedVar.key).value;
      },
      attributeArchived(value: InputVariableViewModel) {
        return !isArchived();
      },
    },
  },
}));

const $v = useVuelidate(validations, { state });
const isTextRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.state.text));
const isOptionsRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.state.options));
const isAttributeMissingInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.storedVariable, 'attributeMissing'),
);
const isAttributeArchivedInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.storedVariable, 'attributeArchived'),
);

onMounted(() => loadFlows());

watch(
  () => props.value,
  () => {
    try {
      state.value = props.value;
      updateValidation();
    } catch (err: any) {
      console.error('DME watch(() => props.value', err);
    }
  },
  { immediate: true },
);

function textUpdated(text: string) {
  if (state.value.text === text) {
    return;
  }

  state.value.text = text;

  if ($v.value.state && $v.value.state.text) {
    $v.value.state.text.$touch();
  }

  updateState();
}

function updateOptionTransition(index: number, updatedOptionTransition: TransitionViewModel) {
  if (!state.value.options) {
    return;
  }

  if (state.value.options[index]) {
    state.value.options[index].transition = updatedOptionTransition;
  }
  updateState();
}

function addOption() {
  if (!state.value.options) {
    state.value.options = [];
  }

  if (!canAddMoreOptions()) {
    return;
  }

  state.value.options.push({
    id: `opt-${nanoid(7)}`,
    text: '',
  });

  updateState();
}

function deleteOption(optionIndex: number) {
  if (!state.value.options) {
    return;
  }
  state.value.options.splice(optionIndex, 1);

  updateState();
}

function setClickedState() {
  hasClicked.value = true;
}

function setUnclickedState() {
  hasClicked.value = false;
  showConfig.value = false;
  transition.value = 'display';

  $v.value.$touch();
}

function showExpandedConfig() {
  hasClicked.value = true;
  showConfig.value = true;
  transition.value = 'none';
}

function hideExpandedConfig() {
  showConfig.value = false;
}

function optionUpdated(optionIndex: number, text: string) {
  if (!state.value.options) {
    return;
  }

  if (!stateOptionHasBeenUpdated(optionIndex, text)) {
    return;
  }

  state.value.options[optionIndex].text = text;
  updateState();
}

function additionalConfigChanged(updatedState: StateViewModel, invalid: boolean) {
  state.value = updatedState;
  additionalConfigInvalid.value = invalid;
  updateState();
}

function variableSelected(variable: InputVariableViewModel) {
  state.value.storedVariable = variable;
  if (state.value.storedVariable.dataType === 'decimal') {
    state.value.pattern = RegexType.DECIMAL;
  } else if (state.value.storedVariable.dataType === 'number') {
    state.value.pattern = RegexType.NUMERIC;
  } else if (isAppUserPhoneNumber()) {
    state.value.pattern = RegexType.PHONE_NUMBER;
  } else if (isAppUserEmail()) {
    state.value.pattern = RegexType.EMAIL;
  } else {
    state.value.pattern = '';
  }
  state.value.allowedAttempts = 1;
  state.value.errorMessage = '';

  updateState();
}

function canAddMoreOptions() {
  return state.value.options && state.value.options.length < 10;
}

function getAddTooltipText() {
  if (!canAddMoreOptions()) {
    return 'You can only display a maximum of 10 options';
  }
  return '';
}

function getDataTypeErrorMessage() {
  if (state.value && state.value.storedVariable && state.value.storedVariable.dataType) {
    if (state.value.storedVariable.dataType === 'decimal') {
      return 'decimal to 2DP';
    } else if (isAppUserPhoneNumber()) {
      return 'phone number';
    } else if (isAppUserEmail()) {
      return 'email';
    }
    return state.value.storedVariable.dataType;
  }
  return '';
}

function isValidTransition(transition: TransitionViewModel): boolean {
  if (currentFlow.value.status === FlowStatus.Live) {
    const flow = flows.value.find((f: FlowListItemViewModel) => f.id === transition.payload);
    if (flow && flow.status !== FlowStatus.Live) {
      return false;
    }
  }
  return true;
}

function isArchived(): boolean {
  if (state.value && state.value.storedVariable) {
    return isAttributeArchived(state.value.storedVariable!.key).value;
  }
  return false;
}

function focusOnInput() {
  textEditor.value?.focusElement();
  textEditor.value?.startEditing();
}

function hasDataType(): boolean {
  return !!(state.value && state.value.storedVariable && state.value.storedVariable.dataType);
}

function stateOptionHasBeenUpdated(optionIndex: number, value: string) {
  if (state.value && state.value.options && state.value.options[optionIndex]) {
    return state.value.options[optionIndex].text !== value;
  }
  return false;
}

function updateState() {
  updateValidation();
  emit('state-updated', state.value);
}

function updateValidation() {
  state.value.invalid = additionalConfigInvalid.value || $v.value.$invalid;
}

function isAppUserPhoneNumber() {
  return (
    state.value.storedVariable &&
    state.value.storedVariable.scope === 'appUser' &&
    state.value.storedVariable.key === 'phoneNumber'
  );
}

function isAppUserEmail() {
  return (
    state.value.storedVariable &&
    state.value.storedVariable.scope === 'appUser' &&
    state.value.storedVariable.key === 'email'
  );
}
</script>

<style scoped lang="scss">
.quickreply-component {
  display: flex;
  flex: 1;
  border-radius: 5px;
}

.component-contents {
  flex-direction: column;
}

.message-part {
  padding-right: 1em;
}

.reply-options-list {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
}

.reply-option-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;

  &:focus-within {
    z-index: 2;
  }
}

.reply-option-row {
  flex: 1;
  display: flex;
  align-items: center;
}

.reply-option-errors {
  flex: 1;
}

.reply-option {
  flex: 1;
}

.reply-option-text {
  border: solid 1px $color-netcall-deep-sea;
  border-radius: 10px;
  padding: 0.2em 0.45em;

  &:focus-within {
    box-shadow: 0 1px 8px 1px rgba(0, 0, 0, 0.2);
  }
  :deep(.editing) {
    border: none;
    box-shadow: none;
  }
}

.reply-option-icon {
  display: flex;
  padding-left: 0.5em;
  padding-right: 0.5em;
  padding-top: 0.25em;
  flex-shrink: 0;

  &.reply-option-arrow {
    color: $color-netcall-salmon;
  }
}

.reply-option-transition {
  flex: 1;
  position: relative;
  border-radius: 10px;
  justify-content: flex-end;
}

.delete {
  margin: 5px;
}

fieldset {
  :deep(.field-body) {
    flex: 2;

    .control {
      flex: 1;
    }
  }
}

.field {
  padding-top: 10px;
}

.expander {
  position: absolute;
  width: 40px;
  height: 100%;
  left: 100%;
  top: 0;
  color: white;

  border-radius: 0 5px 5px 0;
  background-color: $color-netcall-salmon;
  display: flex;
  justify-content: center; /* align horizontal */
  align-items: center;
}

.chevron {
  font-size: 35px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  svg {
    top: 50%;
    transform: translateY(-50%);
    -ms-transform: translateY(-50%);
    -moz-transform: translateY(-50%);
    -webkit-transform: translateY(-50%);
    -o-transform: translateY(-50%);
  }

  &:hover {
    cursor: pointer;
  }
}

.display-enter-active {
  transition: width 0.25s;
}

.display-leave-active {
  transition: width 0.25s;
}

.display-enter,
.display-leave-to {
  width: 10px;
}

.display-enter-to,
.display-leave {
  width: 40px;
}

.add-button {
  &.disabled {
    cursor: normal;
    opacity: 0.6;
  }
}
</style>
